/**
 * PDF utilities — Phase 735.14 (final root-cause architecture).
 *
 * BACKGROUND
 * ----------
 * Two independent bugs pushed us through several revisions of this helper:
 *
 *   Bug A — `?token=` in the URL
 *       Ad-blockers pattern-match query params that look like credentials
 *       (`?token=…`) and return ERR_BLOCKED_BY_CLIENT. Fixed by dropping
 *       the query token and moving to cookie auth.
 *
 *   Bug B — `blob:` URLs opened in a new tab
 *       Blob URLs are page-scoped; the moment the user REFRESHES the new
 *       tab (or reopens the shared link) Chrome invalidates the blob and
 *       reports ERR_BLOCKED_BY_CLIENT. Fixed by opening a real HTTP URL
 *       instead — cookie auth is carried natively by the browser.
 *
 * DECISION MATRIX
 * ---------------
 *   openPdfInBrowser  →  DIRECT HTTP URL  (survives refresh / share)
 *   downloadPdf       →  Blob + `<a download>` (download completes
 *                        instantly, blob can be revoked immediately)
 *   printPdf          →  Blob in a hidden same-page iframe (iframe lives
 *                        in the current page, blob is never lost)
 *   getPdfBlobUrl     →  Blob URL for `<iframe src=…>` inside an in-page
 *                        modal (iframe lives in the same page — refresh
 *                        of the outer app rebuilds the URL anyway)
 *
 * Every module MUST call one of these four helpers. No call site should
 * construct its own axios+blob+window.open dance.
 */

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function getLang() {
  return localStorage.getItem('cleanpro_language') || 'en';
}

function isCapacitorNative() {
  try {
    return !!(
      window.Capacitor &&
      typeof window.Capacitor.isNativePlatform === 'function' &&
      window.Capacitor.isNativePlatform()
    );
  } catch (_e) {
    return false;
  }
}

/**
 * Build an authenticated PDF URL WITHOUT any token query string.
 * Auth travels via the browser's session cookie (same-origin request).
 */
export function buildPdfUrl(path, params = {}) {
  const url = new URL(`${API}${path}`, window.location.origin);
  if (!params.lang) params.lang = getLang();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v);
  });
  return url.toString();
}

/**
 * Fetch a PDF as a Blob using the current session cookie for auth.
 *
 * Always throws (with a descriptive Error) so callers can toast the failure
 * instead of silently opening a blank tab.
 */
async function fetchPdfBlob(path, params = {}) {
  const url = buildPdfUrl(path, params);
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/pdf' },
    cache: 'no-store',
  });
  if (!res.ok) {
    let msg = `PDF request failed (${res.status})`;
    try {
      const errBody = await res.json();
      if (errBody?.detail) msg = typeof errBody.detail === 'string' ? errBody.detail : msg;
    } catch (_e) {
      /* body was not JSON — keep the generic message */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  // Some proxies/gateways strip the MIME type; force it so <iframe> renders it as PDF.
  return blob.type === 'application/pdf'
    ? blob
    : new Blob([blob], { type: 'application/pdf' });
}

// Keep every blob URL we create so we can revoke them after use — piling up
// object URLs is a well-known memory leak on long-lived SPAs.
const _live = new Set();
function _register(url, ttlMs = 5 * 60 * 1000) {
  _live.add(url);
  setTimeout(() => {
    try { URL.revokeObjectURL(url); } catch (_e) { /* already revoked */ }
    _live.delete(url);
  }, ttlMs);
}

/**
 * Fetch a PDF (or any binary attachment) and return a blob: URL ready to
 * be used as an <iframe src=…> or <a href=…>. Callers should revoke the
 * URL when they no longer need it — the utility auto-revokes after 5min
 * anyway to avoid leaks.
 *
 * Accepts either an API-relative path (`/buildings/…/pdf`) or an absolute
 * URL. Passing an absolute URL strips any `?token=` query string first
 * to preserve extension safety.
 */
export async function getPdfBlobUrl(pathOrUrl, params = {}) {
  let url;
  if (/^https?:\/\//i.test(pathOrUrl)) {
    // Absolute URL — strip legacy `token=` param, then append any extra
    // params. Auth is provided via `credentials: 'include'` cookies.
    const u = new URL(pathOrUrl);
    u.searchParams.delete('token');
    if (!u.searchParams.get('lang') && !params.lang) params.lang = getLang();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') u.searchParams.set(k, v);
    });
    url = u.toString();
  } else {
    url = buildPdfUrl(pathOrUrl, params);
  }
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/pdf,image/*,*/*' },
    cache: 'no-store',
  });
  if (!res.ok) {
    let msg = `PDF request failed (${res.status})`;
    try {
      const errBody = await res.json();
      if (errBody?.detail) msg = typeof errBody.detail === 'string' ? errBody.detail : msg;
    } catch (_e) { /* body not JSON */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const finalBlob =
    blob.type && blob.type !== 'application/octet-stream'
      ? blob
      : new Blob([blob], { type: 'application/pdf' });
  const blobUrl = URL.createObjectURL(finalBlob);
  _register(blobUrl);
  return blobUrl;
}

/**
 * Open a PDF for VIEWING inside the app.
 *
 * Phase 735.30 — Root-cause fix (definitive). Prior implementations relied
 * on `<a target="_blank">` synthetic clicks to open a new tab pointing at
 * the direct HTTP URL. Some browser extensions block those synthetic
 * `target="_blank"` navigations with `ERR_BLOCKED_BY_CLIENT` even though a
 * same-origin `fetch()` to the exact same URL succeeds (confirmed via
 * Playwright inside the app — every PDF endpoint returns HTTP 200 with
 * `Content-Type: application/pdf`).
 *
 * The new implementation dispatches a global `emergent:open-pdf` event
 * carrying the resolved HTTP URL. `<PdfViewerHost/>` (mounted once at the
 * app root) picks it up, `fetch()`es the PDF as a Blob with `credentials:
 * 'include'`, and renders it in an iframe INSIDE the current page. No
 * new-tab navigation, no popup blocker interaction, no URL-pattern
 * blocking against navigations.
 *
 * Native (Capacitor) shells cannot embed the PDF plugin in a WebView, so
 * we fall back to opening the direct URL in the system browser there.
 */
export async function openPdfInBrowser(path, params = {}) {
  const url = _buildDirectPdfUrl(path, params);
  if (isCapacitorNative()) {
    // Capacitor / Android WebView — the in-app iframe viewer cannot render
    // application/pdf reliably. Fall through to the system browser.
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { try { document.body.removeChild(a); } catch (_e) { /* ignore */ } }, 100);
    return url;
  }
  const filename = params.filename || _filenameFromPath(path);
  try {
    window.dispatchEvent(new CustomEvent('emergent:open-pdf', {
      detail: { url, filename },
    }));
  } catch (err) {
    console.error('[openPdfInBrowser] dispatch failed', err);
  }
  return url;
}

// Best-effort filename extraction from an API path like
// `/buildings/abc/meetings/xyz/pdf` → `meetings-xyz.pdf`. Extension modules
// can pass an explicit `params.filename` to override.
function _filenameFromPath(path) {
  try {
    const clean = String(path).split('?')[0].replace(/\/pdf$/, '');
    const parts = clean.split('/').filter(Boolean);
    if (parts.length === 0) return 'document.pdf';
    // Use the last two path segments so the filename is descriptive.
    const tail = parts.slice(-2).join('-');
    return `${tail || 'document'}.pdf`;
  } catch (_e) {
    return 'document.pdf';
  }
}

/**
 * Build a direct HTTP URL for a PDF path. Accepts either an API-relative
 * path (`/buildings/…/pdf`) or an already-absolute URL. Always strips any
 * legacy `?token=` param and defaults `disposition=inline` when the caller
 * did not specify one.
 */
function _buildDirectPdfUrl(pathOrUrl, params = {}) {
  const defaults = { disposition: 'inline' };
  const merged = { ...defaults, ...params };
  if (/^https?:\/\//i.test(pathOrUrl)) {
    const u = new URL(pathOrUrl);
    u.searchParams.delete('token');
    if (!u.searchParams.get('lang') && !merged.lang) merged.lang = getLang();
    Object.entries(merged).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') u.searchParams.set(k, v);
    });
    return u.toString();
  }
  return buildPdfUrl(pathOrUrl, merged);
}

/**
 * Download a PDF using an <a download> click on a blob URL.
 */
export async function downloadPdf(path, params = {}, filename = 'document.pdf') {
  const blob = await fetchPdfBlob(path, params);
  const url = URL.createObjectURL(blob);
  _register(url);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    try { document.body.removeChild(a); } catch (_e) { /* ignore */ }
  }, 500);
  return url;
}

/**
 * Print a PDF via a hidden iframe.
 *
 * A blob URL is used so browser extensions cannot intercept the request, and
 * because the iframe is on `blob:` (same JS origin) `contentWindow.print()`
 * always succeeds.  Native (Capacitor) shells cannot invoke the PDF plugin
 * inside a WebView — we fall back to opening the PDF in the system browser.
 */
export async function printPdf(path, params = {}) {
  // Capacitor / Android WebView: iframe print is unreliable — always open
  // in the system browser instead.
  if (isCapacitorNative()) {
    return openPdfInBrowser(path, params);
  }

  const blob = await fetchPdfBlob(path, params);
  const url = URL.createObjectURL(blob);
  _register(url, 2 * 60 * 1000); // keep alive during print dialog

  // Reuse one iframe so repeated prints don't leak DOM nodes.
  let iframe = document.getElementById('cleanpro-pdf-print-frame');
  if (iframe) {
    try { iframe.parentNode && iframe.parentNode.removeChild(iframe); } catch (_e) { /* ignore */ }
  }
  iframe = document.createElement('iframe');
  iframe.id = 'cleanpro-pdf-print-frame';
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.visibility = 'hidden';
  iframe.setAttribute('aria-hidden', 'true');

  let printed = false;
  const firePrint = () => {
    if (printed) return;
    printed = true;
    // Small delay so the PDF viewer plugin renders before the print call.
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        console.warn('[printPdf] iframe.print() blocked, falling back to new tab', err);
        window.open(url, '_blank');
      }
    }, 400);
  };
  iframe.onload = firePrint;
  // Safety net — some browsers (Firefox) never fire onload for PDFs.
  setTimeout(firePrint, 3500);

  iframe.src = url;
  document.body.appendChild(iframe);
  return url;
}
